/**
 * Offline Calendar Storage Service
 * Handles offline storage of appointments, types, and notes using IndexedDB
 * Automatically syncs to Supabase when connection is restored
 */

import type { Appointment, AppointmentType, AppointmentNote } from './appointmentService';

const DB_NAME = 'NoorCalendarOfflineDB';
const DB_VERSION = 1;

interface OfflineAppointment extends Appointment {
  _offline?: boolean;
  _syncStatus?: 'pending' | 'synced' | 'failed';
  _localId?: string;
  _modifiedAt?: number;
}

interface OfflineAppointmentType extends AppointmentType {
  _offline?: boolean;
  _syncStatus?: 'pending' | 'synced' | 'failed';
  _localId?: string;
}

interface OfflineAppointmentNote extends AppointmentNote {
  _offline?: boolean;
  _syncStatus?: 'pending' | 'synced' | 'failed';
  _localId?: string;
}

interface OfflinePhoto {
  id: string;
  file: File;
  appointmentId: string;
  _syncStatus: 'pending' | 'synced' | 'failed';
  _modifiedAt: number;
}

class OfflineCalendarStorage {
  private static instance: OfflineCalendarStorage;
  private db: IDBDatabase | null = null;

  private constructor() {
    this.initDB();
  }

  static getInstance(): OfflineCalendarStorage {
    if (!OfflineCalendarStorage.instance) {
      OfflineCalendarStorage.instance = new OfflineCalendarStorage();
    }
    return OfflineCalendarStorage.instance;
  }

  private async initDB(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        // Create object stores if they don't exist
        if (!db.objectStoreNames.contains('appointments')) {
          const appointmentStore = db.createObjectStore('appointments', { keyPath: 'id' });
          appointmentStore.createIndex('date', 'date', { unique: false });
          appointmentStore.createIndex('syncStatus', '_syncStatus', { unique: false });
        }

        if (!db.objectStoreNames.contains('appointmentTypes')) {
          db.createObjectStore('appointmentTypes', { keyPath: 'id' });
        }

        if (!db.objectStoreNames.contains('appointmentNotes')) {
          const notesStore = db.createObjectStore('appointmentNotes', { keyPath: 'id' });
          notesStore.createIndex('appointmentId', 'appointment_id', { unique: false });
        }

        if (!db.objectStoreNames.contains('photos')) {
          const photoStore = db.createObjectStore('photos', { keyPath: 'id' });
          photoStore.createIndex('appointmentId', 'appointmentId', { unique: false });
          photoStore.createIndex('syncStatus', '_syncStatus', { unique: false });
        }
      };
    });
  }

  private async ensureDB(): Promise<IDBDatabase> {
    if (!this.db) {
      await this.initDB();
    }
    if (!this.db) {
      throw new Error('Failed to initialize IndexedDB');
    }
    return this.db;
  }

  // ==================== APPOINTMENTS ====================

  async saveAppointment(appointment: Appointment, offline: boolean = false): Promise<void> {
    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['appointments'], 'readwrite');
      const store = transaction.objectStore('appointments');

      const offlineAppointment: OfflineAppointment = {
        ...appointment,
        _offline: offline,
        _syncStatus: offline ? 'pending' : 'synced',
        _localId: appointment.id,
        _modifiedAt: Date.now()
      };

      const request = store.put(offlineAppointment);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async getAppointments(userId?: string): Promise<Appointment[]> {
    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['appointments'], 'readonly');
      const store = transaction.objectStore('appointments');
      const request = store.getAll();

      request.onsuccess = () => {
        let appointments = request.result as OfflineAppointment[];
        
        // Filter by user if specified
        if (userId) {
          appointments = appointments.filter(
            apt => apt.user_id === userId || apt.assigned_to_user_id === userId
          );
        }
        
        resolve(appointments);
      };
      request.onerror = () => reject(request.error);
    });
  }

  async getAppointmentsByDate(date: string): Promise<Appointment[]> {
    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['appointments'], 'readonly');
      const store = transaction.objectStore('appointments');
      const index = store.index('date');
      const request = index.getAll(date);

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async deleteAppointment(id: string): Promise<void> {
    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['appointments'], 'readwrite');
      const store = transaction.objectStore('appointments');
      const request = store.delete(id);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async getPendingSyncAppointments(): Promise<OfflineAppointment[]> {
    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['appointments'], 'readonly');
      const store = transaction.objectStore('appointments');
      const index = store.index('syncStatus');
      const request = index.getAll('pending');

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  // ==================== APPOINTMENT TYPES ====================

  async saveAppointmentType(type: AppointmentType, offline: boolean = false): Promise<void> {
    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['appointmentTypes'], 'readwrite');
      const store = transaction.objectStore('appointmentTypes');

      const offlineType: OfflineAppointmentType = {
        ...type,
        _offline: offline,
        _syncStatus: offline ? 'pending' : 'synced',
        _localId: type.id
      };

      const request = store.put(offlineType);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async getAppointmentTypes(): Promise<AppointmentType[]> {
    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['appointmentTypes'], 'readonly');
      const store = transaction.objectStore('appointmentTypes');
      const request = store.getAll();

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async deleteAppointmentType(id: string): Promise<void> {
    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['appointmentTypes'], 'readwrite');
      const store = transaction.objectStore('appointmentTypes');
      const request = store.delete(id);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  // ==================== APPOINTMENT NOTES ====================

  async saveAppointmentNote(note: AppointmentNote, offline: boolean = false): Promise<void> {
    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['appointmentNotes'], 'readwrite');
      const store = transaction.objectStore('appointmentNotes');

      const offlineNote: OfflineAppointmentNote = {
        ...note,
        _offline: offline,
        _syncStatus: offline ? 'pending' : 'synced',
        _localId: note.id
      };

      const request = store.put(offlineNote);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async getAppointmentNotes(appointmentId: string): Promise<AppointmentNote[]> {
    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['appointmentNotes'], 'readonly');
      const store = transaction.objectStore('appointmentNotes');
      const index = store.index('appointmentId');
      const request = index.getAll(appointmentId);

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async deleteAppointmentNote(id: string): Promise<void> {
    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['appointmentNotes'], 'readwrite');
      const store = transaction.objectStore('appointmentNotes');
      const request = store.delete(id);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  // ==================== PHOTOS ====================

  async savePhoto(photo: OfflinePhoto): Promise<void> {
    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['photos'], 'readwrite');
      const store = transaction.objectStore('photos');
      const request = store.put(photo);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async getPhoto(id: string): Promise<OfflinePhoto | null> {
    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['photos'], 'readonly');
      const store = transaction.objectStore('photos');
      const request = store.get(id);

      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(request.error);
    });
  }

  async getPendingSyncPhotos(): Promise<OfflinePhoto[]> {
    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['photos'], 'readonly');
      const store = transaction.objectStore('photos');
      const index = store.index('syncStatus');
      const request = index.getAll('pending');

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async deletePhoto(id: string): Promise<void> {
    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['photos'], 'readwrite');
      const store = transaction.objectStore('photos');
      const request = store.delete(id);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  // ==================== SYNC STATUS ====================

  async markAsSynced(id: string, storeName: 'appointments' | 'appointmentTypes' | 'appointmentNotes' | 'photos'): Promise<void> {
    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([storeName], 'readwrite');
      const store = transaction.objectStore(storeName);
      const getRequest = store.get(id);

      getRequest.onsuccess = () => {
        const record = getRequest.result;
        if (record) {
          record._syncStatus = 'synced';
          record._offline = false;
          const putRequest = store.put(record);
          putRequest.onsuccess = () => resolve();
          putRequest.onerror = () => reject(putRequest.error);
        } else {
          resolve();
        }
      };
      getRequest.onerror = () => reject(getRequest.error);
    });
  }

  async clearAllData(): Promise<void> {
    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['appointments', 'appointmentTypes', 'appointmentNotes', 'photos'], 'readwrite');
      
      transaction.objectStore('appointments').clear();
      transaction.objectStore('appointmentTypes').clear();
      transaction.objectStore('appointmentNotes').clear();
      transaction.objectStore('photos').clear();

      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
    });
  }
}

export default OfflineCalendarStorage;
export type { OfflineAppointment, OfflineAppointmentType, OfflineAppointmentNote, OfflinePhoto };
